import { Injectable } from '@angular/core';
import {
  Statement,
  Program,
  MustacheStatement,
  BlockStatement,
  ContentStatement,
} from '../ast/ast-node.model';
import { BlockStatementNode } from '../ast/block-statement';
import { ContentStatementNode } from '../ast/content-statement';
import { MustacheStatementNode } from '../ast/mustache-statement';
import { ProgramNode } from '../ast/program';
import { TemplateVariable } from '../interfaces/template-variable.interface';
import { templateEditorLogger } from '../logger.instance';
import { CLASSES, SELECTORS } from '../utils/constants';
import { escapeHtml } from '../utils/escape-html';

const logger = templateEditorLogger.extend('dom-renderer');

@Injectable()
export class DomRendererService {
  /**
   * Converte uma AST para uma string HTML para renderização no editor.
   */
  public renderAstToHtml(program: Program, knownVariables: Map<string, TemplateVariable>): string {
    logger.debug('renderAstToHtml', { program });
    const html = program.body
      .map((statement) => {
        switch (statement.type) {
          case 'ContentStatement':
            return escapeHtml((statement as ContentStatement).value).replaceAll('\n', '<br>');
          case 'MustacheStatement':
            return MustacheStatementNode.toHtml(statement as MustacheStatement, knownVariables);
          case 'BlockStatement':
            return BlockStatementNode.toHtml(statement as BlockStatement, knownVariables, (prog) =>
              this.renderAstToHtml(prog, knownVariables),
            );
          default:
            return '';
        }
      })
      .join('');
    logger.debug('renderAstToHtml: result', { html });
    return html;
  }

  /**
   * Constrói uma AST recursivamente a partir de um nó do DOM.
   */
  public buildAstFromDom(node: Node, knownVariables: Map<string, TemplateVariable>): Program {
    logger.debug('buildAstFromDom', { node });
    const statements: Statement[] = [];
    let consecutiveText = '';

    const processBufferedText = () => {
      if (consecutiveText) {
        statements.push(new ContentStatementNode(consecutiveText));
        consecutiveText = '';
      }
    };

    for (const child of Array.from(node.childNodes)) {
      if (child.nodeType === Node.ELEMENT_NODE) {
        const element = child as HTMLElement;

        if (element.classList.contains(CLASSES.TOOLTIP)) {
          continue;
        }

        const isChipOrBlock =
          element.matches(SELECTORS.BLOCK_STATEMENT) || element.matches(SELECTORS.CHIP_WRAPPER);

        if (element.tagName === 'DIV' && !isChipOrBlock) {
          processBufferedText();
          if (statements.length > 0 || node.textContent !== '') {
            consecutiveText += '\n';
          }

          statements.push(...this.buildAstFromDom(child, knownVariables).body);
          continue;
        }

        if (element.tagName === 'BR') {
          consecutiveText += '\n';
          continue;
        }

        if (isChipOrBlock) {
          processBufferedText();
          if (element.matches(SELECTORS.BLOCK_STATEMENT)) {
            const blockNode = BlockStatementNode.parse(
              element,
              (childNode) => this.buildAstFromDom(childNode, knownVariables),
              knownVariables,
            );
            if (blockNode) statements.push(blockNode);

            continue;
          }

          const mustacheNode = MustacheStatementNode.parse(
            element,
            (childNode) => this.buildAstFromDom(childNode, knownVariables).body,
            knownVariables,
          );
          if (mustacheNode) statements.push(mustacheNode);

          continue;
        }

        statements.push(...this.buildAstFromDom(child, knownVariables).body);

        continue;
      }

      if (child.nodeType === Node.TEXT_NODE) {
        const contentNode = ContentStatementNode.parse(child);
        if (contentNode) {
          consecutiveText += contentNode.value;
        }
      }
    }

    processBufferedText();
    const program = new ProgramNode(statements);
    logger.debug('buildAstFromDom finished', { node, program });
    return program;
  }
}
